import type { ChartOptions, DeepPartial } from "lightweight-charts";

/**
 * lightweight-charts dual-theme helper.
 *
 * Returns a partial chart-options object keyed to the active Hearth
 * theme. Used in two shapes:
 *
 *   1. At chart creation: spread into `createChart(el, { ...chartTheme(theme) })`.
 *   2. On theme toggle: pass to `chart.applyOptions(chartTheme(theme))`.
 *
 * Why a helper instead of CSS variables: lightweight-charts renders to a
 * <canvas>, not the DOM, so it doesn't read CSS custom properties. Each
 * theme change has to push a fresh options object through the imperative
 * API. Background stays "transparent" so the underlying card token shows
 * through and the chart inherits the surrounding palette.
 *
 * Color decisions:
 *   - Light: zinc-family neutrals matching the trading-page convention
 *     used everywhere else in the app at sign-off of Phase 4.
 *   - Dark: warm zinc-700/zinc-800-ish neutrals that match the stone-tinted
 *     background tokens. Avoid pure-black gridlines — they look harsh on
 *     a near-black canvas.
 */

export type ChartThemeMode = "light" | "dark";

interface ChartThemeColors {
  readonly text: string;
  readonly grid: string;
  readonly border: string;
}

const LIGHT_COLORS: ChartThemeColors = {
  text: "#52525b", // zinc-500 — readable axis labels on warm-stone surface
  grid: "#e7e5e4", // stone-200
  border: "#d6d3d1", // stone-300
};

const DARK_COLORS: ChartThemeColors = {
  text: "#a8a29e", // stone-400 — bright enough on near-black, soft enough not to glare
  grid: "#3f3f46", // zinc-700-ish, warm-leaning
  border: "#52525b", // zinc-600
};

export function chartThemeColors(mode: ChartThemeMode): ChartThemeColors {
  return mode === "dark" ? DARK_COLORS : LIGHT_COLORS;
}

/**
 * Returns the layout/grid/scale-border subset of ChartOptions for the
 * given theme. Background stays transparent so the host card supplies
 * its own background token.
 */
export function chartThemeOptions(mode: ChartThemeMode): DeepPartial<ChartOptions> {
  const c = chartThemeColors(mode);
  return {
    layout: {
      // background.color: "transparent" lets the surrounding Tailwind
      // background token paint through; the canvas inherits the card.
      textColor: c.text,
    },
    grid: {
      vertLines: { color: c.grid },
      horzLines: { color: c.grid },
    },
    rightPriceScale: { borderColor: c.border },
    timeScale: { borderColor: c.border },
  };
}

/**
 * Phase 16d (2026-05-10): overlay palette for the dashboard's net-worth
 * chart. Used to color index ETF lines (SPY/QQQ/VT/Custom) and the
 * per-account stacked-area mode. Five distinct hues, theme-aware:
 * slightly desaturated in light mode (less harsh against the warm-stone
 * surface), more saturated in dark mode (need to read on near-black).
 *
 * Color order is stable so the same overlay always gets the same hue
 * across renders (consistent visual identity for SPY = blue etc.).
 *
 * Why a fixed 5-color palette: the user can show net-worth + 3 ETFs +
 * custom = 5 lines max in v1. Per-account mode reuses the palette
 * cyclically when account count exceeds 5; that's fine for the v1
 * household scale.
 */
export const OVERLAY_PALETTE_LIGHT: readonly string[] = [
  "#2563eb", // blue-600 — SPY default
  "#7c3aed", // violet-600 — QQQ default
  "#0d9488", // teal-600 — VT default
  "#d97706", // amber-600 — custom slot 1
  "#db2777", // pink-600 — custom slot 2
];
export const OVERLAY_PALETTE_DARK: readonly string[] = [
  "#60a5fa", // blue-400 — SPY default
  "#a78bfa", // violet-400 — QQQ default
  "#2dd4bf", // teal-400 — VT default
  "#fbbf24", // amber-400 — custom slot 1
  "#f472b6", // pink-400 — custom slot 2
];

export function overlayColor(index: number, mode: ChartThemeMode): string {
  const palette =
    mode === "dark" ? OVERLAY_PALETTE_DARK : OVERLAY_PALETTE_LIGHT;
  return palette[index % palette.length] ?? palette[0]!;
}
